import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Folder, Pencil, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui';
import { cn } from '@/lib/cn';
import { useMatters } from '@/contexts/MatterContext';

// Pill-style dropdown per 03-ui-design-language.md "Matter selector dropdown".
// Click toggles a panel listing matters; each row can be selected (sets the
// active matter) or edited (jumps to the matter editor). New matter button
// at the bottom links to the create route.
export function MatterSelector() {
  const { matters, activeMatter, activeMatterId, setActiveMatterId, loading, error } = useMatters();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on outside click or escape, both with the same handler set so the
  // listeners detach together.
  useEffect(() => {
    if (!open) {
      return;
    }
    function handleDocumentClick(event: MouseEvent): void {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const buttonLabel = loading ? 'Loading matters' : (activeMatter?.name ?? 'No matter selected');

  return (
    <div ref={wrapperRef} className="relative">
      <Button
        variant="secondary"
        size="sm"
        className="max-w-[260px] gap-2 rounded-full px-3 py-1.5"
        leftIcon={<Folder className="h-3.5 w-3.5 text-amber-500" />}
        rightSlot={<ChevronDown className="h-3.5 w-3.5 text-stone-400" />}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        disabled={loading && matters.length === 0}
      >
        <span className="truncate text-stone-300">{buttonLabel}</span>
      </Button>

      {open ? (
        <div
          role="listbox"
          className="bg-stone-925 absolute right-0 z-40 mt-2 w-[320px] overflow-hidden rounded-md border border-stone-800 shadow-lg shadow-stone-950/80"
        >
          <div className="max-h-[60vh] overflow-y-auto">
            {error ? (
              <p className="px-3 py-3 text-xs text-red-300">Could not load matters: {error}</p>
            ) : matters.length === 0 ? (
              <p className="px-3 py-3 text-xs text-stone-500">
                No matters yet. Use New matter to create one.
              </p>
            ) : (
              matters.map((matter) => {
                const isActive = matter.id === activeMatterId;
                return (
                  <div
                    key={matter.id}
                    className={cn(
                      'group flex items-start gap-2 border-b border-stone-800/60 px-3 py-2.5 text-left last:border-b-0',
                      isActive ? 'bg-stone-900' : 'hover:bg-stone-900/60',
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setActiveMatterId(matter.id);
                        setOpen(false);
                      }}
                      className="flex-1 text-left"
                    >
                      <p
                        className={cn(
                          'truncate text-sm',
                          isActive ? 'text-amber-400' : 'text-stone-200',
                        )}
                      >
                        {matter.name}
                      </p>
                      <p className="mt-0.5 truncate text-[10px] uppercase tracking-[0.2em] text-stone-500">
                        {[matter.reference, matter.court].filter(Boolean).join(' · ') ||
                          'No reference'}
                      </p>
                    </button>
                    <Link
                      to={`/matters/${matter.id}`}
                      onClick={() => setOpen(false)}
                      aria-label={`Edit ${matter.name}`}
                      className="opacity-0 transition-opacity group-hover:opacity-100"
                    >
                      <Pencil className="h-3.5 w-3.5 text-stone-500 hover:text-amber-400" />
                    </Link>
                  </div>
                );
              })
            )}
          </div>
          <div className="border-t border-stone-800 bg-stone-900/60">
            <Link
              to="/matters/new"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-amber-400 hover:bg-stone-900"
            >
              <Plus className="h-3.5 w-3.5" />
              New matter
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
